import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState, type FC } from 'react';
import type { User } from '@promtver/contracts';

import { confirmTwoFactor as confirmTwoFactorApi, loginWithPassword, type LoginResult } from '../api/auth';
import { clearSession, getSession, setSession, type StoredSession } from '../api/tokenStore';

interface AuthContextValue {
  user: User | null;
  isRestored: boolean;
  login(email: string, password: string): Promise<LoginResult>;
  confirmTwoFactor(challengeId: string, code: string): Promise<void>;
  logout(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [session, setSessionState] = useState<StoredSession | null>(() => getSession());
  const [isRestored] = useState(true);

  const applySession = useCallback((next: StoredSession) => {
    setSession(next);
    setSessionState(next);
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const result = await loginWithPassword(email.trim(), password);
      if (result.kind === 'ok') {
        applySession({
          accessToken: result.session.accessToken,
          refreshToken: result.session.refreshToken,
          expiresAt: Date.now() + result.session.expiresIn * 1000,
          user: result.session.user,
        });
      }
      return result;
    },
    [applySession],
  );

  const confirmTwoFactor = useCallback(
    async (challengeId: string, code: string) => {
      const next = await confirmTwoFactorApi(challengeId, code);
      applySession({
        accessToken: next.accessToken,
        refreshToken: next.refreshToken,
        expiresAt: Date.now() + next.expiresIn * 1000,
        user: next.user,
      });
    },
    [applySession],
  );

  const logout = useCallback(() => {
    clearSession();
    setSessionState(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user: session?.user ?? null, isRestored, login, confirmTwoFactor, logout }),
    [session, isRestored, login, confirmTwoFactor, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth используется вне AuthProvider');
  }
  return context;
}
